import { useEffect, useId, useRef } from 'react'
import { brParaIso, formatarDataBr, isoParaBr, validarDataBr } from '@precad/compartilhado/validacao/data'

/**
 * Campo de data: digitação com máscara MAIS um botão de calendário.
 *
 * A primeira versão era um `<input type="date">` controlado, e ela ZERAVA o
 * campo no meio da digitação. O motivo: enquanto a data está incompleta, o
 * elemento reporta `value === ''`. O estado virava vazio, o React reescrevia
 * `''` no elemento, e os segmentos já digitados sumiam. Controlar um input de
 * data com um valor derivado é brigar com o navegador.
 *
 * Aqui quem manda é o texto com máscara — que já funcionava — e o calendário é
 * um input SEPARADO e NÃO CONTROLADO, que só existe para reportar a escolha.
 * Ele nunca recebe `value` pelo React; a sincronização é imperativa, num
 * efeito, justamente para não disputar o elemento com a digitação.
 *
 * Os dois juntos, e não um ou outro: quem sabe a data digita `15/08/1980` mais
 * rápido do que navega décadas para trás num calendário.
 */

/* `showPicker` é o que abre o calendário sem exibir o input. Sem suporte, o
   botão não é desenhado — botão morto é pior que botão ausente. */
const TEM_PICKER =
  typeof HTMLInputElement !== 'undefined' &&
  typeof HTMLInputElement.prototype.showPicker === 'function'

export function CampoData({
  rotulo,
  valor,
  aoMudar,
  erro,
  opcional,
  /** Impede escolher data futura. Certo para nascimento. */
  ateHoje,
}: {
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  erro?: string | null
  opcional?: boolean
  ateHoje?: boolean
}) {
  const id = useId()
  const idAuxiliar = `${id}-aux`
  const refCalendario = useRef<HTMLInputElement>(null)

  /* Sincroniza o calendário com o texto de forma IMPERATIVA. Se isto fosse a
     prop `value`, voltaríamos ao bug: o React reescreveria o elemento a cada
     tecla. */
  useEffect(() => {
    const el = refCalendario.current
    if (el) el.value = validarDataBr(valor) ? brParaIso(valor) : ''
  }, [valor])

  const hoje = new Date()
  const maximo = ateHoje
    ? `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`
    : undefined

  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={id}>
        {rotulo}
        {opcional ? <span className="campo__opcional">opcional</span> : null}
      </label>

      <div className="campo__linha">
        <input
          id={id}
          className="campo__entrada"
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder="DD/MM/AAAA"
          value={valor}
          onChange={(e) => aoMudar(formatarDataBr(e.target.value))}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? idAuxiliar : undefined}
        />

        {TEM_PICKER ? (
          <button
            type="button"
            className="campo__botao"
            aria-label={`Escolher ${rotulo} no calendário`}
            onClick={() => refCalendario.current?.showPicker?.()}
          >
            <span aria-hidden="true">📅</span>
          </button>
        ) : null}

        {/* NÃO controlado de propósito: só reporta a escolha. `tabIndex={-1}`
            para o teclado não parar num campo invisível. */}
        <input
          ref={refCalendario}
          type="date"
          className="campo__calendario"
          tabIndex={-1}
          aria-hidden="true"
          min="1900-01-01"
          max={maximo}
          onChange={(e) => {
            const br = isoParaBr(e.target.value)
            if (br) aoMudar(br)
          }}
        />
      </div>

      {erro ? (
        <p className="campo__erro" id={idAuxiliar}>
          <span aria-hidden="true">⚠</span>
          {erro}
        </p>
      ) : null}
    </div>
  )
}
